import * as Yup from 'yup'
import {BaseEntity} from '../../../../shared/models/ApiModels'

/** Adayın cevabından beklenen koşulun operatörü */
export type QuestionConditionOperator =
  | 'EQUALS'
  | 'NOT_EQUALS'
  | 'ONE_OF'
  | 'NOT_ONE_OF'
  | 'CONTAINS'
  | 'GREATER_THAN_OR_EQUAL'
  | 'LESS_THAN_OR_EQUAL'
  | 'IS_TRUE'
  | 'IS_FALSE'
  | 'NOT_EMPTY'

/** Operatörün kaç değer beklediği */
export type ConditionValueArity = 'NONE' | 'SINGLE' | 'MULTI'

/** question tablosu */
export interface IQuestionModel extends BaseEntity {
  label: string
  inputType: string
  /** Başvuruda boş bırakılamaz */
  required: boolean
  /** RADIO ve SELECT tiplerinin seçenekleri */
  options: string[]
  /** Boş ise soru için koşul tanımlanmamıştır */
  conditionOperator?: QuestionConditionOperator | null
  conditionValues: string[]
  /** Koşul sağlanmazsa adaya bu şablonla mail gönderilir */
  failMailTemplateId?: string | null
  failMailTemplateName?: string | null
}

/**
 * Adayın cevaplayacağı alanın tipi. Backend inputType'ı metin olarak tutuyor;
 * ekranda bu listeden seçilir. Listede olmayan eski değerler olduğu gibi gösterilir.
 */
export const QUESTION_INPUT_TYPES: {value: string; label: string}[] = [
  {value: 'TEXT', label: 'Kısa Metin'},
  {value: 'TEXTAREA', label: 'Uzun Metin'},
  {value: 'NUMBER', label: 'Sayı'},
  {value: 'DATE', label: 'Tarih'},
  {value: 'BOOLEAN', label: 'Evet / Hayır'},
  {value: 'RADIO', label: 'Tek Seçim'},
  {value: 'SELECT', label: 'Açılır Liste'},
]

/** Seçenek listesi gerektiren tipler */
export const CHOICE_INPUT_TYPES = ['RADIO', 'SELECT']

export const isChoiceType = (inputType: string) => CHOICE_INPUT_TYPES.includes(inputType)

export const getInputTypeLabel = (inputType: string) =>
  QUESTION_INPUT_TYPES.find((type) => type.value === inputType)?.label ?? inputType

/** Koşul operatörleri; arity değerleri backend QuestionConditionOperatorEnum ile aynıdır */
export const CONDITION_OPERATORS: {
  value: QuestionConditionOperator
  label: string
  arity: ConditionValueArity
  /** Yalnızca bu cevap tiplerinde gösterilir; boşsa tüm tiplerde geçerli */
  inputTypes?: string[]
}[] = [
  {value: 'EQUALS', label: 'Eşittir', arity: 'SINGLE'},
  {value: 'NOT_EQUALS', label: 'Eşit değildir', arity: 'SINGLE'},
  {value: 'ONE_OF', label: 'Şunlardan biri', arity: 'MULTI'},
  {value: 'NOT_ONE_OF', label: 'Şunlardan biri değil', arity: 'MULTI'},
  {value: 'CONTAINS', label: 'İçerir', arity: 'SINGLE', inputTypes: ['TEXT', 'TEXTAREA']},
  {
    value: 'GREATER_THAN_OR_EQUAL',
    label: 'Büyük veya eşit',
    arity: 'SINGLE',
    inputTypes: ['NUMBER', 'DATE'],
  },
  {
    value: 'LESS_THAN_OR_EQUAL',
    label: 'Küçük veya eşit',
    arity: 'SINGLE',
    inputTypes: ['NUMBER', 'DATE'],
  },
  {value: 'IS_TRUE', label: 'Evet olmalı', arity: 'NONE', inputTypes: ['BOOLEAN']},
  {value: 'IS_FALSE', label: 'Hayır olmalı', arity: 'NONE', inputTypes: ['BOOLEAN']},
  {value: 'NOT_EMPTY', label: 'Boş olmamalı', arity: 'NONE'},
]

export const conditionOperatorsFor = (inputType: string) =>
  CONDITION_OPERATORS.filter(
    (operator) => !operator.inputTypes || operator.inputTypes.includes(inputType)
  )

export const conditionArity = (operator: string): ConditionValueArity =>
  CONDITION_OPERATORS.find((item) => item.value === operator)?.arity ?? 'SINGLE'

export const conditionOperatorLabel = (operator?: string | null) =>
  CONDITION_OPERATORS.find((item) => item.value === operator)?.label ?? operator ?? ''

/** Koşulun ekranda okunabilir özeti: "Eşittir: Evet" */
export const conditionSummary = (question: IQuestionModel) => {
  if (!question.conditionOperator) return ''
  const label = conditionOperatorLabel(question.conditionOperator)
  const values = question.conditionValues || []
  return values.length > 0 ? `${label}: ${values.join(', ')}` : label
}

/** Formda seçenekler her satıra bir tane olacak şekilde düz metin olarak düzenlenir */
export interface IQuestionFormValues {
  label: string
  inputType: string
  required: boolean
  optionsText: string
  /** Boş string: koşul yok */
  conditionOperator: string
  /** SINGLE operatörlerde kullanılır */
  conditionValue: string
  /** MULTI operatörlerde kullanılır */
  conditionValues: string[]
  failMailTemplateId: string
}

export const parseOptions = (optionsText: string) =>
  optionsText
    .split('\n')
    .map((option) => option.trim())
    .filter((option, index, all) => option.length > 0 && all.indexOf(option) === index)

export const validationQuestion = Yup.object().shape({
  label: Yup.string()
    .trim()
    .required('Soru zorunludur')
    .max(255, 'Soru en fazla 255 karakter olabilir'),
  inputType: Yup.string().required('Cevap tipi zorunludur'),
  optionsText: Yup.string().when('inputType', {
    is: (inputType: string) => isChoiceType(inputType),
    then: (schema: Yup.StringSchema) =>
      schema.test('min-options', 'En az iki seçenek giriniz', (value) =>
        value ? parseOptions(value).length >= 2 : false
      ),
  }),
  conditionValue: Yup.string().when('conditionOperator', {
    is: (operator: string) => !!operator && conditionArity(operator) === 'SINGLE',
    then: (schema: Yup.StringSchema) => schema.trim().required('Koşul değeri zorunludur'),
  }),
  conditionValues: Yup.array()
    .of(Yup.string())
    .test('multi-condition-values', 'En az bir koşul değeri seçiniz', function (values) {
      const operator = this.parent?.conditionOperator
      if (!operator || conditionArity(operator) !== 'MULTI') return true
      return !!values && values.length > 0
    }),
  failMailTemplateId: Yup.string(),
})

export const initialQuestionForm: IQuestionFormValues = {
  label: '',
  inputType: 'TEXT',
  required: false,
  optionsText: '',
  conditionOperator: '',
  conditionValue: '',
  conditionValues: [],
  failMailTemplateId: '',
}

export const toQuestionFormValues = (question: IQuestionModel): IQuestionFormValues => {
  const operator = question.conditionOperator || ''
  const values = question.conditionValues || []
  return {
    label: question.label,
    inputType: question.inputType,
    required: !!question.required,
    optionsText: (question.options || []).join('\n'),
    conditionOperator: operator,
    conditionValue: conditionArity(operator) === 'SINGLE' ? values[0] ?? '' : '',
    conditionValues: conditionArity(operator) === 'MULTI' ? values : [],
    failMailTemplateId: question.failMailTemplateId ?? '',
  }
}

export const toQuestionModel = (values: IQuestionFormValues): IQuestionModel => {
  const operator = (values.conditionOperator || null) as QuestionConditionOperator | null
  const arity = operator ? conditionArity(operator) : 'NONE'
  const conditionValues = !operator
    ? []
    : arity === 'SINGLE'
    ? [String(values.conditionValue ?? '').trim()].filter((value) => value.length > 0)
    : arity === 'MULTI'
    ? values.conditionValues
    : []

  return {
    label: values.label.trim(),
    inputType: values.inputType,
    required: values.required,
    options: isChoiceType(values.inputType) ? parseOptions(values.optionsText) : [],
    conditionOperator: operator,
    conditionValues,
    failMailTemplateId: operator ? values.failMailTemplateId || null : null,
  }
}
